//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// BCGrowls
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// A simple javascript class that allows for the addition of simple growls/toasts to a web app.
// There are no outside dependencies for the class exept it's companion .css file.  Basic 508 compatibility
// including markup for screen reader support is included.
// Files: bcGrowls.js and bcGrowls.css
//
// Author: C Adam Smith
// For: NRCS/ASRC/CPDES
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
export class BCGrowls {

    // NOTES
    //-------------------------------------------------------------------------------------------------------------------------
    // - Currently, this control always places the growls at the top right of the screen (use the offset settings to adjust it)


    // PRIVATE PROPERTIES
    //-------------------------------------------------------------------------------------------------------------------------
    // Parameters
    _growlBoxId = null;
    _settings = {};

    // Logical
    _idCounter = 1;
    _growlBoxDiv = null;    //Reference to the growlbox div

    _svgPaths = {
        error: "M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z",
        info: "M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z",
        success: "M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z",
        warn: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z",
        close: "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
    }


    // INITIALIZATION
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // constructor
    //==================================
    // growlBoxId<string>:         //(req) Uniqe id for the growlBox
    constructor(growlBoxId){

        //Validate ID
        if (!growlBoxId) { throw (`[BCGrowls:constructor()] growlBoxId required`) }

        this._growlBoxId = growlBoxId;
    };

    //==================================
    // init
    //==================================
    init(settings) {

        console.log(`[BCGrowls:init()] - settings (passed in)`, settings);

        // Settings
        //--------------------
        this._settings = {
            appendToElementId: null,                //<str, opt> Id of the element to insert the growl box container into
                                                    //  Growl container will be prepended to the top of document if this id is omitted
                                                    //  IMPORTANT NOTE: This container does NOT control the location of the growls on the screen
                                                    //  It is used to determine it's position relative to the rest of the elements on the DOM
                                                    //  primarily for the purpose of tab stop order.

            defaultFadeDuration: 2000,              //<int, opt> MS duration of fade out for when growls close automatically

            defaultRollupDuration: 200,             //<int, opt> MS duration of roll up for when growls close

            defaultLifeSpans: {                     //<int, opt> default ms before this type of growl is automatically faded out (0 = indefinite)
                error: 0,                           //Used when a growl is added without a lifespan specified
                info: 4000,
                success: 4000,
                warn: 0
            },             
            
            iconSize: 24,                           //<int, opt> All icons will be square

            iconDefaultFillColor: "#282828",        //<str, opt> Currently applies to all icons' fill color

            offsetTop: 55,                          //<int, opt> Number of pixels to push the growls down from the top of the screen
            offsetRight: 10                         //<int, opt> Number of pixels to push the growls left from the right side of the screen
        }

        //Adopt user settings
        Object.assign(this._settings, settings);

        //Settings Validation
        if (this.isNullOrUndefined(this._settings.defaultLifeSpans.error)) { throw "[BCGrowls:init()] _settings.defaultLifeSpans.error missing.  Make sure your settings don't accidentally remove this property." }
        if (this.isNullOrUndefined(this._settings.defaultLifeSpans.info)) { throw "[BCGrowls:init()] _settings.defaultLifeSpans.info missing.  Make sure your settings don't accidentally remove this property." }
        if (this.isNullOrUndefined(this._settings.defaultLifeSpans.success)) { throw "[BCGrowls:init()] _settings.defaultLifeSpans.success missing.  Make sure your settings don't accidentally remove this property." }
        if (this.isNullOrUndefined(this._settings.defaultLifeSpans.warn)) { throw "[BCGrowls:init()] _settings.defaultLifeSpans.warn missing.  Make sure your settings don't accidentally remove this property." }


        // Main Growl Container
        //------------------------------
        this._growlBoxDiv = document.createElement("div");
        this._growlBoxDiv.id = this._growlBoxId;
        this._growlBoxDiv.className = "bcGrowls_main_div";

        //Apply the offsets
        this._growlBoxDiv.style.top = `${this._settings.offsetTop}px`;
        this._growlBoxDiv.style.right = `${this._settings.offsetRight}px`;

        //Add the div to the screen
        if (this._settings.appendToElementId) {
            let targetContainer = document.getElementById(this._settings.appendToElementId);
            if (!targetContainer) {
                throw (`[BCGrowls:init()] Cannot find element referred to by settings.appendToElementId: ${this._settings.appendToElementId}`)
            }

            //All good, add it in
            targetContainer.appendChild(this._growlBoxDiv);

        }
        else {

            //No target specified, so put it at the top of the document
            document.body.prepend(this._growlBoxDiv);
        }
        
    }

    // PUBLIC FUNCTIONS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // addGrowl
    //==================================
    //  growlType<str>:     //(req) chose one: info, success, warn, error
    //  message<str>:       //(req) the message you wan to relay
    //  lifespan<int>:      //(opt, settings.defaultLifeSpans.growlType) ms before removing the growl, if 0, does not expire ("persistent").
    addGrowl(growlType, message, lifespan) {

        // Validate
        //----------------
        if ("|info|success|warn|error|".indexOf(`|${growlType}|`) < 0) {
            throw (`[BCGrowls:addGrowl()] growlType parameter must be one of: info, success, warn, error`)
        }

        // Defaults
        //------------
        lifespan = this.isNullOrUndefined(lifespan) ? this._settings.defaultLifeSpans[growlType] : lifespan;

        // Setup
        //----------------
        //Capture the current, active element (for focus return when the growl is closed manually)
        let returnElement = document.activeElement;

        //Generate an id
        let growlId = `${this._growlBoxId}_bcGrowl_${this._idCounter++}`;

        //Create the div to contain the growl
        let growlDiv = document.createElement("div");
        growlDiv.id = growlId;

        //Ad the common class
        growlDiv.className = "bcGrowls_growl_div";

        //Add the growl type specific class
        growlDiv.classList.add(`bcGrowls_growl_div_${growlType}`);


        // Icon Column
        //---------------------
        let iconDiv = document.createElement("div");
        //iconDiv.className = "bcGrowls_growl_div_col_icon";
        iconDiv.classList.add("bcGrowls_growl_div_col", "bcGrowls_growl_div_col_icon");

        //Create the vector image
        let iconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        iconSvg.setAttribute('viewbox', `0 0 ${this._settings.iconSize} ${this._settings.iconSize}`);
        iconSvg.setAttribute('width', `${this._settings.iconSize}px`);
        iconSvg.setAttribute('height', `${this._settings.iconSize}px`);

        //Draw the selected path
        let iconPath = document.createElementNS("http://www.w3.org/2000/svg", 'path');
        iconPath.setAttribute('d', this._svgPaths[growlType]);
        iconPath.setAttribute('fill', this._settings.iconDefaultFillColor);

        //Let em rip
        iconSvg.appendChild(iconPath);
        iconDiv.append(iconSvg);


        // Message Column
        //---------------------
        let msgDiv = document.createElement("div");
        msgDiv.setAttribute("tabindex", 0);
        msgDiv.classList.add("bcGrowls_growl_div_col", "bcGrowls_growl_div_col_msg");
        msgDiv.innerHTML = message;

        //508: Add alert attributes
        // Since this div is outside the purview of Angular or other "smart" frameworks, it should not trigger/render repeatedly
        // in the Jaws reader if we flag it as an alert.
        msgDiv.setAttribute("aria-live", "assertive"); //Prioritize these above all other chatter
        msgDiv.setAttribute("role", "alert");
        
        //508: Add a hidden bit of text for Jaws to read
        let jawsDiv = document.createElement("div");
        jawsDiv.style = "position:absolute; left:-10000px; top:auto; width:1px; height:1px; overflow:hidden;"
        jawsDiv.innerHTML = `Growl alert, ${this.growlTypeToText(growlType)},`;
        msgDiv.prepend(jawsDiv);


        // Controls Column
        //---------------------
        let ctrlDiv = document.createElement("div");
        ctrlDiv.tabIndex = 0
        ctrlDiv.setAttribute("aria-label", `Press the enter key to close growl alert`);
        ctrlDiv.classList.add("bcGrowls_growl_div_col", "bcGrowls_growl_div_col_controls");
        ctrlDiv.setAttribute("role", "button");
        ctrlDiv.title = "Close growl";

        // Close immediately if the user clicks the "X"/Div while the div has focus
        ctrlDiv.addEventListener("click", () => { 

            this.closeGrowl(growlId, 0);

            //Jump back to triggering element.  Don't wait on the animation to finish
            if (document.body.contains(returnElement)) { returnElement.focus(); }

        }, false);

        // Close immediately if the user presses enter while the "X"/Div has focus
        ctrlDiv.addEventListener("keydown", (event) => { 
            if (event.keyCode == 13) { 

                this.closeGrowl(growlId, 0);
                
                //Jump back to triggering element.  Don't wait on the animation to finish
                if (document.body.contains(returnElement)) { returnElement.focus(); }

            }}, false);

        //Create the vector image
        let controlSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        controlSvg.setAttribute('viewbox', `0 0 ${this._settings.iconSize} ${this._settings.iconSize}`);
        controlSvg.setAttribute('width', `${this._settings.iconSize}px`);
        controlSvg.setAttribute('height', `${this._settings.iconSize}px`);

        //Draw the close path
        let closePath = document.createElementNS("http://www.w3.org/2000/svg", 'path');
        closePath.setAttribute('d', this._svgPaths.close);
        closePath.setAttribute('fill', this._settings.iconDefaultFillColor);

        //Let em rip
        controlSvg.appendChild(closePath);
        ctrlDiv.append(controlSvg);
        
        // Append the divs
        //--------------------
        growlDiv.append(iconDiv);
        growlDiv.append(msgDiv);
        growlDiv.append(ctrlDiv);
        this._growlBoxDiv.append(growlDiv);

        // Apply lifespan
        //--------------------
        if (lifespan > 0) {
            window.setTimeout(() => {
                this.closeGrowl(growlId);
            }, lifespan);
        }

        // Set focus
        //--------------------
        // If this is a "persistent" popup, then shift the focus to the message div (BASECAMP-323: 508 Defect)
        // When the user clicks/presses the close button, they will be returned to the element they camefrom 
        // (if it's still present in the document)
        if (lifespan == 0) {
            msgDiv.focus();
        }

    }

    // PRIVATE FUNCTIONS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // closeGrowl
    //==================================
    // growlId<str>:            //full element id
    // fadeDuration<int>:       //(opt, settings.defaultFadeDuration) Set to 0 to instantly close the growl
    // rollupDuration<int>:     //(opt, settings.defaultRollupDuration) Set to 0 to instantly rollup the growl
    // fnOnClose<fn>:           //(opt, pass a function to be executed when the close is complete)
    closeGrowl(growlId, fadeDuration, rollupDuration, fnOnClose) {

        //Check params
        fadeDuration = this.isNullOrUndefined(fadeDuration) ? this._settings.defaultFadeDuration : fadeDuration;
        rollupDuration = this.isNullOrUndefined(rollupDuration) ? this._settings.defaultRollupDuration : rollupDuration;

        //Find the growl element
        let el = document.getElementById(growlId);
        if (!el) { return; } //Jump out if something has already removed it

        //Just remove it if our fadeDuration is 0
        if (fadeDuration == 0) {
            this.rollupElement(growlId, rollupDuration, true, () => {
                if (fnOnClose) { fnOnClose(); }
            });
            return;
        }

        //Fade element out and then roll it up
        this.fadeElement(growlId, fadeDuration, false, () => {
            this.rollupElement(growlId, rollupDuration, true, () => {
                if (fnOnClose) { fnOnClose(); }
            });
        });
    }

    //================================================
    // fadeElement
    //================================================
    fadeElement(elId, duration, bRemove, fnOnAnimationComplete) {

        //Run the fade loop
        let stepSize = 50;  //Update every stepSize ms (50 seems to produce smooth results)
        let increment = 1.0 / (duration / stepSize);  //Figure out how much to drop the alpha per step to complete in the given duration
        let alpha = 1.0;
        let el;

        let intervalId = window.setInterval(() => {
            
            //Reacquire the element to make sure it's still in the document
            el = document.getElementById(elId);
            
            //Jump out if the element has been removed since the last iteration
            if (!el) {
                window.clearInterval(intervalId);
                return;
            }

            //Calc the new alpha
            alpha = alpha - increment;

            //Check for the exit condition
            if (alpha < 0) { 

                //Remove element?
                if (bRemove && el) { el.remove(); } //This should also remove any event listeners if there are no outstanding references to the element

                //Stop loop
                window.clearInterval(intervalId);

                //Inform caller
                if (fnOnAnimationComplete) { fnOnAnimationComplete(); }
            }

            //Fade the element
            el.style.opacity = alpha;

        }, stepSize);

    }

    //================================================
    // growlTypeToText
    //================================================
    // I added this later because the growl types weren't quite enough alone to create proper aria-labels for Jaws
    //  growlType<str>:     //(req) chose one: info, success, warn, error
    growlTypeToText(growlType) {

        switch (growlType.toLowerCase()) {
            case "error": return "Error";
            case "info": return "Information";
            case "success": return "Success";
            case "warn": return "Warning";
            default: throw (`[BCGrowls:growlTypeToText()] growlType parameter must be one of: info, success, warn, error`)
        }
    }

    //================================================
    // rollupElement
    //================================================
    rollupElement(elId, duration, bRemove, fnOnAnimationComplete) {

        //This method does not animate vertical padding in the target element.  Either exclude padding from the
        //target element, or wrap it in another element that has no padding (overflow hidden recommended if the element is visible)

        //Grab the element
        let el = document.getElementById(elId);
        if (!el) { return; } //Jump out if something has already removed it

        //Setup
        let stepSize = 20;  //Set step size //How often we update in ms
        let elHeight = el.offsetHeight; //Get the current size in pixels (INCLUDES padding in the total)
        let numSteps = duration / stepSize;

        //Determine the amount to reduce height per step in pixels (let's keep it linear, no need for fancy pants here)
        let increment = elHeight / numSteps;

        let intervalId = window.setInterval(() => {
            
            //Reacquire the element to make sure it's still in the document
            el = document.getElementById(elId);
            
            //Jump out if the element has been removed since the last iteration
            if (!el) {
                window.clearInterval(intervalId);
                return;
            }

            //Calc the new height
            elHeight = elHeight - increment;

            //Check for the exit condition
            if (elHeight < 0) { 

                //Remove element?
                if (bRemove && el) { 
                    el.remove(); //This should also remove any event listeners if there are no outstanding references to the element
                }

                //Stop loop
                window.clearInterval(intervalId);

                //Inform caller
                if (fnOnAnimationComplete) { fnOnAnimationComplete(); }
            }

            //Resize the element
            el.style.height = `${elHeight}px`;


        }, stepSize);

    }

    //================================================
    // isNullOrUndefined
    //================================================
    //  Returns true for values that are null or undefined.
    //  All other values will return a false (including "" and 0)
	isNullOrUndefined(val) {
        return (typeof (val) === "undefined" || val === null) ? true : false;
	};

}
